import { onMounted, onUnmounted, ref } from 'vue'
import type { Ref } from 'vue'

// Flips `revealed` to true once the target enters the viewport, then stops observing.
// When the visitor prefers reduced motion (or IntersectionObserver is missing) the
// content is revealed immediately so nothing animates and nothing stays hidden.
export function useRevealOnScroll(): { target: Ref<HTMLElement | null>, revealed: Ref<boolean> } {
  const target = ref<HTMLElement | null>(null)
  const revealed = ref(false)
  let observer: IntersectionObserver | null = null

  onMounted(() => {
    const prefersReducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined' || target.value === null) {
      revealed.value = true
      return
    }

    observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        revealed.value = true
        observer?.disconnect()
        observer = null
      }
    }, { threshold: 0.15 })
    observer.observe(target.value)
  })

  onUnmounted(() => {
    observer?.disconnect()
    observer = null
  })

  return { target, revealed }
}
